import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { hasRdProfileImage } from "../../Dietitian/registrationMatchedRd";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalCloseBtn,
  ModalBody,
  RdOptionCard,
  RdAvatar,
  RdInfo,
  RdName,
  RdCreds,
  RdTags,
  RdTag,
  RdActions,
  EmptyMessage,
} from "./RegistrationNoPreferenceRdSlotModal.style";

const RdSlotModalAvatar = ({ rd }) => {
  const [imageError, setImageError] = useState(false);
  const hasImage = hasRdProfileImage(rd.profileImage);
  const showImage = hasImage && !imageError;

  useEffect(() => {
    setImageError(false);
  }, [rd.id, rd.profileImage]);

  return (
    <RdAvatar style={showImage ? undefined : { background: rd.avatarStyle }}>
      {showImage ? (
        <img
          src={rd.profileImage}
          alt=""
          onError={() => setImageError(true)}
          onLoad={(event) => {
            if (event.currentTarget.naturalWidth === 0) {
              setImageError(true);
            }
          }}
        />
      ) : (
        rd.initials
      )}
    </RdAvatar>
  );
};

const RegistrationNoPreferenceRdSlotModal = ({
  isOpen,
  onClose,
  slotLabel,
  dietitians,
  onViewProfile,
  onBookAppointment,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <ModalOverlay role="presentation" onClick={onClose}>
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="no-pref-rd-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader>
          <div>
            <ModalTitle id="no-pref-rd-modal-title">
              {t("NUTRITION_RDPREFMODAL_LBL_TITLE")}
            </ModalTitle>
            {slotLabel ? (
              <ModalSubtitle>{slotLabel}</ModalSubtitle>
            ) : null}
          </div>
          <ModalCloseBtn
            type="button"
            onClick={onClose}
            aria-label={t("NUTRITION_COMMON_ARIA_CLOSE")}
          >
            ✕
          </ModalCloseBtn>
        </ModalHeader>

        <ModalBody>
          {dietitians.length === 0 ? (
            <EmptyMessage>
              {t("NUTRITION_RDPREFMODAL_DESC_NO_AVAILABLE")}
            </EmptyMessage>
          ) : (
            dietitians.map((rd) => (
              <RdOptionCard key={rd.id}>
                <RdSlotModalAvatar rd={rd} />
                <RdInfo>
                  <RdName>{rd.name}</RdName>
                  {rd.creds ? <RdCreds>{rd.creds}</RdCreds> : null}
                  {rd.tags.length > 0 ? (
                    <RdTags>
                      {rd.tags.map((tag) => (
                        <RdTag key={tag}>{tag}</RdTag>
                      ))}
                    </RdTags>
                  ) : null}
                  <RdActions>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => onViewProfile(rd)}
                    >
                      {t("NUTRITION_RDPREFMODAL_BTN_VIEW_PROFILE")}
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => onBookAppointment(rd)}
                    >
                      {t("NUTRITION_RDPREFMODAL_BTN_BOOK")}
                    </button>
                  </RdActions>
                </RdInfo>
              </RdOptionCard>
            ))
          )}
        </ModalBody>
      </ModalPanel>
    </ModalOverlay>,
    document.body
  );
};

export default RegistrationNoPreferenceRdSlotModal;
